"use client";
import SectionHeader from "@/components/SectionHeader";
import { Images } from "@assets/assets";
import Image from "next/image";
import React, { useState } from "react";

interface IndustrySolutionProps {
  title: React.ReactNode | string;
  subtitle: string;
  paragraph: string;
  dark?: boolean;
  solutionArray?: Array<any>;
  showFeatureIcon?: boolean;
}

export default function Industrysolution({
  title,
  subtitle,
  paragraph,
  dark = true,
  solutionArray = [],
  showFeatureIcon = true,
}: IndustrySolutionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  return (
    <section
      id="industry-solution"
      className="industry-solution bg-[#000000] pb-[60px] md:pb-[100px] lg:pb-[120px] w-full relative"
    >
      <div className="max-w-[100vw] overflow-hidden relative">
        <div className="texture-bg mix-blend-overlay  w-full h-full absolute top-0 left-0 z-0">
          {" "}
          <div className="absolute inset-0 z-0">
            <Image
              src={Images?.texture}
              alt="texture"
              fill
              className="object-cover mix-blend-overlay opacity-60"
              sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
            />
          </div>
        </div>
        <div className="w-[400px] h-[400px] md:w-[600px] md:h-[600px] lg:w-[907.05px] lg:h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[200px] md:blur-[280px] lg:blur-[333.68px] absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3" />
        <div className="w-[400px] h-[400px] md:w-[600px] md:h-[600px] lg:w-[907.05px] lg:h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[200px] md:blur-[280px] lg:blur-[333.68px] absolute left-0 bottom-0 -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_5%)] sm:w-[calc(100%_-_8%)] md:w-[calc(100%_-_10%)] py-[40px] md:py-[60px] lg:pt-[120px] relative z-1">
        <SectionHeader
          title={title}
          subtitle={subtitle}
          paragraph={paragraph}
          dark={dark}
          className="pb-[20px] md:pb-[30px] lg:pb-[40px] border-b border-white/10 mb-[40px] md:mb-[60px] lg:mb-[80px]"
        />

        {/* Mobile: Card Layout - All solutions visible */}
        <div className="lg:hidden flex flex-col gap-[40px] md:gap-[50px]">
          {solutionArray?.map((item, index) => {
            return (
              <>
                <div
                  key={item.title}
                  className="flex flex-col gap-[20px] md:gap-[25px] w-full"
                >
                  {/* Card Header with Number and Title */}
                  <div className="flex items-start gap-[15px] md:gap-[20px]">
                    <div
                      className="flex flex-col items-center"
                      style={{ width: 35, minWidth: 35 }}
                    >
                      <div
                        className="text-[#636364] clash-font"
                        style={{
                          fontSize: "clamp(14px, 3vw, 18px)",
                          lineHeight: 1.2,
                        }}
                      >
                        {(index + 1).toString().padStart(2, "0")}
                      </div>
                    </div>
                    <h3
                      className="text-white text-[18px] sm:text-[20px] md:text-[24px] clash-font"
                      style={{
                        lineHeight: 1.2,
                      }}
                    >
                      {item.title}
                    </h3>
                  </div>

                  {/* Card Image */}
                  <div className="w-full hidden">
                    <Image
                      src={item.images}
                      alt={item.title}
                      className="w-full h-full object-cover rounded-lg"
                      width={1200}
                      height={800}
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  </div>

                  {/* Card Description */}
                  <p className="text-white/70 text-[14px] sm:text-[15px] md:text-[16px] leading-[1.6]">
                    {item.description}
                  </p>

                  {/* Card Features */}
                  <div className="flex flex-wrap gap-[10px] md:gap-[12px]">
                    {item.features?.map((feature: any) => (
                      <div
                        key={feature.label}
                        className="flex items-center gap-2 rounded-full 
                              border border-white/10 bg-white/10 backdrop-blur-[21px]
                              px-[10px] py-[6px] md:px-[12px] md:py-[7px]"
                      >
                        {showFeatureIcon && (
                          <Image
                            src={feature.icon}
                            alt=""
                            className="w-[16px] h-[16px] md:w-[18px] md:h-[18px] opacity-90"
                            width={18}
                            height={18}
                            sizes="(max-width: 1024px) 100vw, 50vw"
                          />
                        )}
                        <span
                          className="text-white text-[12px] md:text-[13px]"
                          dangerouslySetInnerHTML={{
                            __html: feature.label ?? "",
                          }}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Divider (except for last item) */}
                  {index !== solutionArray.length - 1 && (
                    <div className="w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mt-[20px]" />
                  )}
                </div>
              </>
            );
          })}
        </div>

        {/* Desktop: Tab Layout */}
        <div className="hidden lg:grid mt-6 md:mt-8 lg:mt-10 gap-6 md:gap-8 lg:grid-cols-2 place-items-start relative">
          {/* Left: Tabs */}
          <div className="relative w-full">
            <div
              role="tablist"
              aria-label="Restaurant solutions"
              className="flex flex-col gap-[30px] md:gap-[40px] lg:gap-[50px]"
            >
              {solutionArray?.map((item, index) => (
                <button
                  role="tab"
                  aria-selected={activeIndex === index}
                  aria-controls={`solution-panel-${index}`}
                  id={`solution-tab-${index}`}
                  aria-label="solution-tab"
                  key={item.title}
                  onClick={() => setActiveIndex(index)}
                  className={`flex items-start gap-[15px] md:gap-[20px] lg:gap-[30px]
                            pb-[20px] md:pb-[25px] lg:pb-[30px]
                            text-left w-full group relative 
                            ${index !== solutionArray.length - 1 ? 'after:content-[""] after:absolute after:left-[15px] md:after:left-[18px] lg:after:left-[22px] after:top-[25px] md:after:top-[28px] lg:after:top-[30px] after:w-[2px] after:h-full after:bg-gradient-to-b after:from-white/20 after:to-white/10' : ""}
                                `}
                >
                  <div
                    className="flex flex-col items-center"
                    style={{ width: 35, minWidth: 35 }}
                  >
                    <div
                      className="text-[#636364] clash-font"
                      style={{
                        fontSize: "clamp(14px, 3vw, var(--space-15-20))",
                        lineHeight: 1.2,
                      }}
                    >
                      {(index + 1).toString().padStart(2, "0")}
                    </div>
                    <div className="w-px grow bg-white/20 mt-2 hidden md:block" />
                  </div>
                  <div
                    className={`text-white ${activeIndex === index ? "opacity-100" : "opacity-50 group-hover:opacity-80"} transition-opacity duration-300`}
                  >
                    <h3
                      className="text-[16px] sm:text-[18px] md:text-[20px] lg:text-[24px] xl:text-[clamp(24px,2vw,32px)] clash-font"
                      style={{
                        lineHeight: 1.2,
                        maxWidth: "30ch",
                      }}
                    >
                      {item.title}
                    </h3>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right: Sticky panel */}
          <div
            role="tabpanel"
            id={`solution-panel-${activeIndex}`}
            aria-labelledby={`solution-tab-${activeIndex}`}
            className="flex flex-col gap-[20px] md:gap-[25px] lg:gap-[30px] w-full lg:sticky lg:top-12"
          >
            {/* Media grid */}
            <div className="grid grid-cols-1 gap-4">
              <Image
                src={solutionArray[activeIndex]?.images[0] || ""}
                alt=""
                className="w-full h-full object-cover rounded-lg md:rounded-xl"
                width={1200}
                height={800}
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>

            <p
              className="text-white/70 text-[14px] sm:text-[15px] md:text-[16px] lg:text-[length:var(--space-15-18)] leading-[1.6]"
              dangerouslySetInnerHTML={{
                __html: solutionArray[activeIndex]?.description ?? "",
              }}
            />

            <div className="flex flex-wrap gap-[10px] md:gap-[12px] lg:gap-[15px]">
              {solutionArray[activeIndex]?.features?.map((feature: any) => (
                <div
                  key={feature.label}
                  className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 backdrop-blur-[21px] px-[10px] py-[6px] md:px-[12px] md:py-[7px] lg:px-[14px] lg:py-[8px]"
                >
                  {showFeatureIcon && (
                    <Image
                      src={feature.icon}
                      alt=""
                      className="w-[16px] h-[16px] md:w-[18px] md:h-[18px] opacity-90"
                      width={18}
                      height={18}
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  )}
                  <span
                    className="text-white text-[12px] md:text-[13px] lg:text-[14px]"
                    dangerouslySetInnerHTML={{ __html: feature.label ?? "" }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
